import { Injectable, NgZone } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { interval, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class PwaService {
    private intervalSubscription: Subscription | null = null;

    constructor(
        private swUpdate: SwUpdate,
        private snackBar: MatSnackBar,
        private zone: NgZone
    ) {
        this.initializeUpdateChecking();
    }

    private initializeUpdateChecking(): void {
        if (!this.swUpdate.isEnabled) {
            console.log('Service Worker ist nicht aktiviert.');
            return;
        }

        // Check for updates on service worker initialization
        this.checkForUpdate();

        // Subscribe to version updates
        this.swUpdate.versionUpdates.subscribe(event => {
            if (event.type === 'VERSION_READY') {
                this.promptUserToUpdate(event);
            }
        });

        // Set up periodic update checks
        this.setupPeriodicUpdateChecks();
    }

    private checkForUpdate(): void {
        this.swUpdate
            .checkForUpdate()
            .then(() => console.log('Update-Prüfung abgeschlossen.'))
            .catch(err => console.error('Fehler bei der Update-Prüfung:', err));
    }

    private setupPeriodicUpdateChecks(): void {
        this.zone.runOutsideAngular(() => {
            this.intervalSubscription = interval(15 * 60 * 1000) // every 15 minutes
                .pipe(takeUntil(this.swUpdate.unrecoverable))
                .subscribe(() => {
                    this.zone.run(() => this.checkForUpdate());
                });
        });
    }

    private promptUserToUpdate(event: VersionReadyEvent): void {
        const snackBarRef = this.snackBar.open(
            'Eine neue Version ist verfügbar.',
            'Update installieren',
            {
                duration: 0, // Doesn't auto-dismiss
                panelClass: ['update-snackbar'],
            }
        );

        snackBarRef.onAction().subscribe(() => {
            this.applyUpdate();
        });
    }

    private applyUpdate(): void {
        this.swUpdate.activateUpdate().then(() => {
            window.location.reload();
        });
    }

    ngOnDestroy(): void {
        if (this.intervalSubscription) {
            this.intervalSubscription.unsubscribe();
        }
    }
}
